import React, { useState, useEffect } from "react";
import { Plus, UserCheck, Pencil, Trash2, ShieldCheck } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { useClientPagination } from "../../hooks/useClientPagination";
import {
  helpers as api,
  units as unitsApi,
  uploads as uploadsApi,
  type Helper,
  type Unit,
  type ApiError,
} from "../../services/api";
import DataGrid from "../../components/DataGrid";
import SearchSelect from "../../components/SearchSelect";
import AuthedImage from "../../components/AuthedImage";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};
const actionBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
  color: "var(--nx-text-muted)",
  display: "inline-flex",
  alignItems: "center",
};

const INIT = {
  name: "",
  phone: "",
  role: "OTHER" as Helper["role"],
  unitId: "",
  active: true,
};

const HelpersPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [items, setItems] = useState<Helper[]>([]);
  const [unitList, setUnitList] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Helper | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmHelper, setConfirmHelper] = useState<Helper | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [idProofUrl, setIdProofUrl] = useState("");
  const [photoLocalPreview, setPhotoLocalPreview] = useState("");
  const [idProofLocalPreview, setIdProofLocalPreview] = useState("");
  const [idProofIsPdf, setIdProofIsPdf] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingIdProof, setUploadingIdProof] = useState(false);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewTitle, setPreviewTitle] = useState<string>("");

  useEffect(() => {
    return () => {
      if (photoLocalPreview) URL.revokeObjectURL(photoLocalPreview);
    };
  }, [photoLocalPreview]);
  useEffect(() => {
    return () => {
      if (idProofLocalPreview) URL.revokeObjectURL(idProofLocalPreview);
    };
  }, [idProofLocalPreview]);

  const { rows, page, setPage, totalPages } = useClientPagination(items, 20);

  useEffect(() => {
    if (!societyId) {
      setLoading(false);
      return;
    }
    api
      .list(societyId)
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
    unitsApi
      .list(societyId)
      .then((page) => setUnitList(page.content))
      .catch(() => {});
  }, [societyId]);

  const unitLabel = (id?: string) =>
    unitList.find((u) => u.id === id)?.unitNumber || "Society-wide";

  const openAdd = () => {
    setEditing(null);
    setFormError("");
    setForm(INIT);
    setPhotoUrl("");
    setIdProofUrl("");
    setPhotoLocalPreview("");
    setIdProofLocalPreview("");
    setIdProofIsPdf(false);
    setShowForm(true);
  };
  const openEdit = (h: Helper) => {
    setEditing(h);
    setFormError("");
    setForm({
      name: h.name,
      phone: h.phone,
      role: h.role,
      unitId: h.unitId || "",
      active: h.active,
    });
    setPhotoUrl(h.photoUrl || "");
    setIdProofUrl(h.idProofUrl || "");
    setPhotoLocalPreview("");
    setIdProofLocalPreview("");
    setIdProofIsPdf(false);
    setShowForm(true);
  };
  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(INIT);
    setFormError("");
    setPhotoUrl("");
    setIdProofUrl("");
    setPhotoLocalPreview("");
    setIdProofLocalPreview("");
    setIdProofIsPdf(false);
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !societyId) return;
    setPhotoLocalPreview(URL.createObjectURL(file));
    setUploadingPhoto(true);
    try {
      const { url } = await uploadsApi.upload(societyId, file);
      setPhotoUrl(url);
    } catch {
      /* ignore */
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleIdProofChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !societyId) return;
    setIdProofIsPdf(file.type === "application/pdf");
    setIdProofLocalPreview(URL.createObjectURL(file));
    setUploadingIdProof(true);
    try {
      const { url } = await uploadsApi.upload(societyId, file);
      setIdProofUrl(url);
    } catch {
      /* ignore */
    } finally {
      setUploadingIdProof(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true);
    setFormError("");
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        role: form.role,
        unitId: form.unitId || undefined,
        active: form.active,
        photoUrl: photoUrl || undefined,
        idProofUrl: idProofUrl || undefined,
      };
      if (editing) {
        const updated = await api.update(societyId, editing.id, payload);
        setItems((p) => p.map((h) => (h.id === updated.id ? updated : h)));
      } else {
        const created = await api.create(societyId, payload);
        setItems((p) => [created, ...p]);
      }
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to save helper.");
    } finally {
      setSaving(false);
    }
  };

  const openPreview = (url: string, title: string) => {
    setPreviewUrl(url);
    setPreviewTitle(title);
  };

  const closePreview = () => {
    setPreviewUrl("");
    setPreviewTitle("");
  };

  const verify = async (h: Helper, status: Helper["backgroundCheckStatus"]) => {
    if (!societyId) return;
    setVerifyingId(h.id);
    try {
      const updated = await api.verify(societyId, h.id, status);
      setItems((p) => p.map((x) => (x.id === updated.id ? updated : x)));
    } catch {
      /* ignore */
    } finally {
      setVerifyingId(null);
    }
  };

  const bgColor = (s: Helper["backgroundCheckStatus"]) =>
    s === "VERIFIED"
      ? { bg: "rgba(16,185,129,0.1)", text: "#10B981" }
      : s === "REJECTED"
        ? { bg: "rgba(239,68,68,0.1)", text: "#DC2626" }
        : { bg: "rgba(245,158,11,0.1)", text: "#D97706" };

  const handleDelete = (h: Helper) => {
    setConfirmHelper(h);
    setDeleteError("");
  };

  const confirmDelete = async () => {
    if (!societyId || !confirmHelper) return;
    setDeletingId(confirmHelper.id);
    try {
      await api.delete(societyId, confirmHelper.id);
      setItems((p) => p.filter((h) => h.id !== confirmHelper.id));
      setConfirmHelper(null);
    } catch {
      setDeleteError("Failed to remove helper.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--nx-text-primary)",
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            Helpers
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {items.length} registered helpers · whitelisted numbers auto-enter
            at the gate
          </p>
        </div>
        <button
          onClick={openAdd}
          className="nx-btn nx-btn-primary"
          style={{ gap: 6 }}
        >
          <Plus size={14} /> Add Helper
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            {
              key: "name",
              header: "Name",
              cellStyle: { fontWeight: 500 },
              render: (h: Helper) => h.name,
            },
            {
              key: "phone",
              header: "Phone",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (h: Helper) => h.phone,
            },
            {
              key: "role",
              header: "Role",
              render: (h: Helper) =>
                h.role.charAt(0) + h.role.slice(1).toLowerCase(),
            },
            {
              key: "unit",
              header: "Unit",
              cellStyle: { color: "var(--nx-text-muted)" },
              render: (h: Helper) => unitLabel(h.unitId),
            },
            {
              key: "status",
              header: "Status",
              render: (h: Helper) => (
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 99,
                    fontSize: 10,
                    fontWeight: 500,
                    background: h.active
                      ? "rgba(16,185,129,0.1)"
                      : "rgba(148,163,184,0.1)",
                    color: h.active ? "#10B981" : "#64748B",
                  }}
                >
                  {h.active ? "Active" : "Inactive"}
                </span>
              ),
            },
            {
              key: "photo",
              header: "Photo",
              render: (h: Helper) =>
                h.photoUrl ? (
                  <button
                    type="button"
                    onClick={() =>
                      openPreview(h.photoUrl!, `${h.name} - Photo`)
                    }
                    style={{
                      border: "none",
                      background: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AuthedImage
                      societyId={societyId!}
                      fileUrl={h.photoUrl}
                      alt={`${h.name} photo`}
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid var(--nx-border)",
                      }}
                    />
                  </button>
                ) : (
                  <span style={{ color: "var(--nx-text-muted)" }}>—</span>
                ),
            },
            {
              key: "idProof",
              header: "ID Proof",
              render: (h: Helper) =>
                h.idProofUrl ? (
                  <button
                    type="button"
                    onClick={() =>
                      openPreview(h.idProofUrl!, `${h.name} - ID Proof`)
                    }
                    style={{
                      border: "none",
                      background: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <AuthedImage
                      societyId={societyId!}
                      fileUrl={h.idProofUrl}
                      alt={`${h.name} id proof`}
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: "cover",
                        borderRadius: 8,
                        border: "1px solid var(--nx-border)",
                      }}
                    />
                  </button>
                ) : (
                  <span style={{ color: "var(--nx-text-muted)" }}>—</span>
                ),
            },
            {
              key: "bgCheck",
              header: "Background Check",
              render: (h: Helper) => {
                const bc = bgColor(h.backgroundCheckStatus);
                return (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 500,
                      background: bc.bg,
                      color: bc.text,
                    }}
                  >
                    {h.backgroundCheckStatus}
                  </span>
                );
              },
            },
            {
              key: "actions",
              header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (h: Helper) => (
                <>
                  {h.backgroundCheckStatus !== "VERIFIED" && (
                    <button
                      onClick={() => verify(h, "VERIFIED")}
                      disabled={verifyingId === h.id}
                      style={actionBtn}
                      title="Mark verified"
                    >
                      <ShieldCheck size={14} />
                    </button>
                  )}
                  <button
                    onClick={() => openEdit(h)}
                    style={actionBtn}
                    title="Edit helper"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(h)}
                    disabled={deletingId === h.id}
                    style={{
                      ...actionBtn,
                      color: "#EF4444",
                      opacity: deletingId === h.id ? 0.4 : 1,
                    }}
                    title="Remove helper"
                  >
                    <Trash2 size={14} />
                  </button>
                </>
              ),
            },
          ]}
          rows={rows}
          rowKey={(h) => h.id}
          loading={loading}
          emptyIcon={
            <UserCheck size={32} style={{ color: "var(--nx-text-muted)" }} />
          }
          emptyText="No helpers registered yet."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>

      <ConfirmDialog
        open={confirmHelper !== null}
        variant="danger"
        title={`Remove "${confirmHelper?.name}"?`}
        message="This helper will no longer be auto-approved at the gate."
        confirmLabel="Remove Helper"
        isLoading={deletingId !== null}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmHelper(null);
          setDeleteError("");
        }}
      />

      {previewUrl && (
        <Modal
          title={previewTitle}
          submitLabel="Close"
          error={undefined}
          isLoading={false}
          onClose={closePreview}
          onSubmit={(e) => {
            e.preventDefault();
            closePreview();
          }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <AuthedImage
              societyId={societyId!}
              fileUrl={previewUrl}
              alt={previewTitle}
              style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: 14 }}
            />
          </div>
        </Modal>
      )}

      {showForm && (
        <Modal
          title={editing ? "Edit Helper" : "Add Helper"}
          submitLabel={editing ? "Save Changes" : "Add Helper"}
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          <div>
            <label style={lbl}>Name *</label>
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              required
              placeholder="Lakshmi"
              style={inp}
              autoFocus
            />
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Phone *</label>
              <input
                value={form.phone}
                onChange={(e) =>
                  setForm((p) => ({ ...p, phone: e.target.value }))
                }
                required
                placeholder="9876543210"
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Role</label>
              <select
                value={form.role}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    role: e.target.value as Helper["role"],
                  }))
                }
                style={inp}
              >
                <option value="MAID">Maid</option>
                <option value="DRIVER">Driver</option>
                <option value="COOK">Cook</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
          </div>
          <div>
            <label style={lbl}>Unit</label>
            <SearchSelect
              value={form.unitId}
              onChange={(v) => setForm((p) => ({ ...p, unitId: v }))}
              options={unitList.map((u) => ({
                value: u.id,
                label: u.unitNumber,
              }))}
              placeholder="Society-wide (any unit)"
            />
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select
              value={form.active ? "true" : "false"}
              onChange={(e) =>
                setForm((p) => ({ ...p, active: e.target.value === "true" }))
              }
              style={inp}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Photo</label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={inp}
              />
              {photoLocalPreview ? (
                <img
                  src={photoLocalPreview}
                  alt=""
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    objectFit: "cover",
                    marginTop: 6,
                  }}
                />
              ) : (
                photoUrl && (
                  <AuthedImage
                    societyId={societyId!}
                    fileUrl={photoUrl}
                    alt=""
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      objectFit: "cover",
                      marginTop: 6,
                    }}
                  />
                )
              )}
              {uploadingPhoto && (
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--nx-text-muted)",
                    marginTop: 4,
                  }}
                >
                  Uploading…
                </p>
              )}
            </div>
            <div>
              <label style={lbl}>ID Proof</label>
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleIdProofChange}
                style={inp}
              />
              {idProofLocalPreview ? (
                idProofIsPdf ? (
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--nx-text-muted)",
                      marginTop: 4,
                    }}
                  >
                    PDF selected.
                  </p>
                ) : (
                  <img
                    src={idProofLocalPreview}
                    alt=""
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      objectFit: "cover",
                      marginTop: 6,
                    }}
                  />
                )
              ) : (
                idProofUrl &&
                (/\.pdf($|\?)/i.test(idProofUrl) ? (
                  <p
                    style={{
                      fontSize: 11,
                      color: "var(--nx-text-muted)",
                      marginTop: 4,
                    }}
                  >
                    PDF uploaded.
                  </p>
                ) : (
                  <AuthedImage
                    societyId={societyId!}
                    fileUrl={idProofUrl}
                    alt=""
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      objectFit: "cover",
                      marginTop: 6,
                    }}
                  />
                ))
              )}
              {uploadingIdProof && (
                <p
                  style={{
                    fontSize: 11,
                    color: "var(--nx-text-muted)",
                    marginTop: 4,
                  }}
                >
                  Uploading…
                </p>
              )}
            </div>
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default HelpersPage;
